import { apiFetch } from "@/lib/api-fetch";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import type {
  WhatsAppContatoIgnorado,
  WhatsAppItem,
  WhatsAppMetricas,
  WhatsAppResumo,
  WhatsAppItemCategoria,
  WhatsAppItemStatus,
  WhatsAppMotivoDescarte,
  WhatsAppRun,
  WhatsAppTranscript,
  WhatsAppAguardando,
  WhatsAppResumoFreelancer,
  WhatsAppResumoLista,
  WhatsAppBuscaMensagens,
  WhatsAppDiaDoResumo,
  WhatsAppSurfaceResumo,
  WhatsAppVersaoResumo,
  WhatsAppSugestoes,
  WhatsAppSurface,
  WhatsAppTrecho,
} from "@/types/whatsapp";

const BASE = SUPABASE_URL.replace(/\/$/, "");

function url(path: string): string {
  return `${BASE}${path}`;
}

export const whatsappApi = {
  // ---- Runs ---------------------------------------------------------------

  listRuns(params: { limit?: number; offset?: number } = {}): Promise<{
    runs: WhatsAppRun[];
    total: number;
  }> {
    const q = new URLSearchParams();
    q.set("limit", String(params.limit ?? 50));
    q.set("offset", String(params.offset ?? 0));
    return apiFetch(url(`/whatsapp/runs?${q.toString()}`));
  },

  getRun(runId: string): Promise<WhatsAppRun> {
    return apiFetch(url(`/whatsapp/runs/${runId}`));
  },

  listItemsDaRun(
    runId: string,
    params: { categoria?: WhatsAppItemCategoria; status?: WhatsAppItemStatus } = {},
  ): Promise<{ items: WhatsAppItem[] }> {
    const q = new URLSearchParams();
    if (params.categoria) q.set("categoria", params.categoria);
    if (params.status) q.set("status", params.status);
    const qs = q.toString();
    return apiFetch(url(`/whatsapp/runs/${runId}/items${qs ? `?${qs}` : ""}`));
  },

  /** Dispara o processamento das mensagens sincronizadas (roda em background). */
  processar(windowDays = 7): Promise<{
    run_id: string;
    window_start: string;
    window_end: string;
  }> {
    return apiFetch(url("/whatsapp/processar"), {
      method: "POST",
      body: JSON.stringify({ window_days: windowDays }),
    });
  },

  /** Itens de todas as rodadas (a fila e o histórico são do contato, não da
   * janela). `status` aceita lista: "aprovado,rejeitado". */
  listItems(
    params: {
      status?: string;
      categoria?: WhatsAppItemCategoria;
      limit?: number;
      offset?: number;
    } = {},
  ): Promise<{ items: WhatsAppItem[] }> {
    const q = new URLSearchParams();
    if (params.status) q.set("status", params.status);
    if (params.categoria) q.set("categoria", params.categoria);
    q.set("limit", String(params.limit ?? 300));
    q.set("offset", String(params.offset ?? 0));
    return apiFetch(url(`/whatsapp/items?${q.toString()}`));
  },

  getResumo(): Promise<WhatsAppResumo> {
    return apiFetch(url("/whatsapp/resumo"));
  },

  getMetricas(limit = 30): Promise<WhatsAppMetricas> {
    return apiFetch(url(`/whatsapp/metricas?limit=${limit}`));
  },

  // ---- Revisão ------------------------------------------------------------

  aprovar(
    itemId: string,
    body: {
      posicao_id?: string;
      payload_corrigido?: Record<string, unknown>;
      surface?: WhatsAppSurface;
    } = {},
  ): Promise<{ id: string; review_status: string; applied_ref: unknown }> {
    return apiFetch(url(`/whatsapp/items/${itemId}/aprovar`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  rejeitar(
    itemId: string,
    body: {
      motivo?: WhatsAppMotivoDescarte;
      nota?: string;
      surface?: WhatsAppSurface;
    } = {},
  ): Promise<{ id: string; review_status: string }> {
    return apiFetch(url(`/whatsapp/items/${itemId}/rejeitar`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  associarFreelancer(
    itemId: string,
    body: { freelancer_id: string; aplicar_aos_demais?: boolean },
  ): Promise<{ id: string; freelancer_id: string; freelancer_nome: string; items_atualizados: number }> {
    return apiFetch(url(`/whatsapp/items/${itemId}/associar-freelancer`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  aprovarLote(itemIds: string[]): Promise<{
    aprovados: string[];
    falhas: { id: string; reason: string }[];
  }> {
    return apiFetch(url("/whatsapp/items/aprovar-lote"), {
      method: "POST",
      body: JSON.stringify({ item_ids: itemIds }),
    });
  },

  // ---- Contatos ignorados ---------------------------------------------------

  ignorarContato(
    conversationId: string,
    motivo: string,
  ): Promise<{ conversation_id: string; itens_rejeitados: number }> {
    return apiFetch(url(`/whatsapp/conversations/${conversationId}/ignorar`), {
      method: "POST",
      body: JSON.stringify({ motivo }),
    });
  },

  reativarContato(
    conversationId: string,
  ): Promise<{ conversation_id: string; reativada: boolean }> {
    return apiFetch(url(`/whatsapp/conversations/${conversationId}/reativar`), {
      method: "POST",
    });
  },

  listContatosIgnorados(): Promise<{ contatos: WhatsAppContatoIgnorado[] }> {
    return apiFetch(url("/whatsapp/conversations/ignoradas"));
  },

  // ---- Conversa / histórico -----------------------------------------------

  getTranscript(conversationId: string): Promise<WhatsAppTranscript> {
    return apiFetch(url(`/whatsapp/conversations/${conversationId}/transcript`));
  },

  listSentimentos(freelancerId: string): Promise<{ sentimentos: unknown[] }> {
    return apiFetch(url(`/whatsapp/freelancers/${freelancerId}/sentimentos`));
  },

  // ---- Conferência fora da fila (F9) --------------------------------------

  /** Janela de conversa em volta das mensagens que sustentam o item. */
  trecho(itemId: string): Promise<WhatsAppTrecho> {
    return apiFetch(url(`/whatsapp/items/${itemId}/trecho`));
  },

  /** O que a IA concluiu sobre um freelancer, para a ficha dele. */
  sugestoesDoFreelancer(freelancerId: string): Promise<WhatsAppSugestoes> {
    return apiFetch(url(`/whatsapp/freelancers/${freelancerId}/sugestoes`));
  },

  /** Em que pé está a conversa com o freelancer, em texto corrido. */
  resumoDoFreelancer(freelancerId: string): Promise<WhatsAppResumoFreelancer> {
    return apiFetch(url(`/whatsapp/freelancers/${freelancerId}/resumo`));
  },

  /** Resumos vigentes de todas as conversas, da mais recente para a mais antiga. */
  listResumos(
    params: { aguardando?: WhatsAppAguardando; limit?: number; offset?: number } = {},
  ): Promise<WhatsAppResumoLista> {
    const q = new URLSearchParams();
    if (params.aguardando) q.set("aguardando", params.aguardando);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    const qs = q.toString();
    return apiFetch(url(`/whatsapp/resumos${qs ? `?${qs}` : ""}`));
  },

  /** Refaz do zero o resumo das conversas, em background. */
  regerarResumos(conversationIds?: string[]): Promise<{ conversas: number }> {
    return apiFetch(url("/whatsapp/resumos/regerar"), {
      method: "POST",
      body: JSON.stringify({ conversation_ids: conversationIds ?? null }),
    });
  },

  /** "Está certo": a pessoa conferiu esta versão do resumo. */
  confirmarResumo(summaryId: string, surface: WhatsAppSurfaceResumo): Promise<{ id: string }> {
    return apiFetch(url(`/whatsapp/resumos/${summaryId}/confirmar`), {
      method: "POST",
      body: JSON.stringify({ surface }),
    });
  },

  /** "Corrigir": a versão da pessoa vira o resumo vigente. */
  corrigirResumo(
    summaryId: string,
    body: {
      historico: WhatsAppDiaDoResumo[];
      ultimo_status: string | null;
      nota: string | null;
      surface: WhatsAppSurfaceResumo;
    },
  ): Promise<{ resumo_id: string }> {
    return apiFetch(url(`/whatsapp/resumos/${summaryId}/corrigir`), {
      method: "POST",
      body: JSON.stringify(body),
    });
  },

  versoesDoResumo(
    conversationId: string,
  ): Promise<{ conversation_id: string; versoes: WhatsAppVersaoResumo[] }> {
    return apiFetch(url(`/whatsapp/conversations/${conversationId}/resumos`));
  },

  /** Busca textual nas mensagens do espelho, sem acento e sem caixa. */
  buscarMensagens(q: string, freelancerId?: string): Promise<WhatsAppBuscaMensagens> {
    const params = new URLSearchParams({ q });
    if (freelancerId) params.set("freelancer_id", freelancerId);
    return apiFetch(url(`/whatsapp/mensagens/busca?${params.toString()}`));
  },

  conferir(
    itemId: string,
    surface: WhatsAppSurface = "ficha_freelancer",
  ): Promise<{ id: string; verdict: string }> {
    return apiFetch(url(`/whatsapp/items/${itemId}/conferir`), {
      method: "POST",
      body: JSON.stringify({ surface }),
    });
  },

  corrigir(
    itemId: string,
    body: {
      valor: string;
      motivo?: WhatsAppMotivoDescarte;
      nota?: string;
      surface?: WhatsAppSurface;
    },
  ): Promise<{ id: string; verdict: string }> {
    return apiFetch(url(`/whatsapp/items/${itemId}/corrigir`), {
      method: "POST",
      body: JSON.stringify({ surface: "ficha_freelancer", ...body }),
    });
  },
};
